import { AnalyticsDetailsResponse } from "@queries/Analytics";
import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import { GraphStatistics } from "src/pages/report/Interfaces";

interface Analytics {
  data: (AnalyticsDetailsResponse & {
    key: string;
  })[];
  order: number;
}

export interface ReportContentTitles {
  title: string;
  order: number;
}

interface ReportState {
  reportId: string;
  graphStatistics: GraphStatistics | undefined;
  analyticsData: Analytics[];
  analyticsBackupData: Analytics[];
  analyticsLoading: boolean;
  callAnalyticsQueueApi: boolean;
  analyticsQueueData: Analytics[];
  analyticsQueueBackupData: Analytics[];
  initialReportContentTitleList: ReportContentTitles[];
  reportContentTitleList: ReportContentTitles[];
}

const initialState: ReportState = {
  reportId: "",
  graphStatistics: undefined,
  analyticsData: [],
  analyticsBackupData: [],
  analyticsLoading: false,
  callAnalyticsQueueApi: false,
  analyticsQueueData: [],
  analyticsQueueBackupData: [],
  initialReportContentTitleList: [],
  reportContentTitleList: []
};

const reportSlice = createSlice({
  name: "report",
  initialState,
  reducers: {
    setActiveReport: (
      state,
      action: PayloadAction<ReportState["reportId"]>
    ) => {
      return {
        ...state,
        reportId: action.payload
      };
    },
    setActiveReportGraphStatistics: (
      state,
      action: PayloadAction<ReportState["graphStatistics"]>
    ) => {
      return {
        ...state,
        graphStatistics: action.payload
      };
    },
    setActiveReportAnalyticsData: (state, action: PayloadAction<Analytics>) => {
      const existingIndex = state.analyticsData?.findIndex(
        (item) => item.order === action.payload.order
      );

      if (existingIndex !== -1 && existingIndex !== undefined) {
        // Update existing item
        const updatedAnalyticsData = [...state.analyticsData];
        updatedAnalyticsData[existingIndex] = action.payload;
        return {
          ...state,
          analyticsData: updatedAnalyticsData
        };
      }

      // If no matching order found, append
      return {
        ...state,
        analyticsData: state.analyticsData
          ? [...state.analyticsData, action.payload]
          : [action.payload]
      };
    },
    resetAnalyticsData: (state) => {
      return {
        ...state,
        analyticsData: [],
        analyticsBackupData: [],
        analyticsQueueData: [],
        analyticsQueueBackupData: [],
        graphStatistics: undefined
      };
    },
    analyticsLoadingAction: (state, action) => {
      return {
        ...state,
        analyticsLoading: action.payload
      };
    },
    setBackupAnalytics: (state, action: PayloadAction<Analytics>) => {
      return {
        ...state,
        analyticsBackupData: state.analyticsBackupData
          ? [...state.analyticsBackupData, action.payload]
          : [action.payload]
      };
    },
    setAnalyticsWithBackup: (state) => {
      return {
        ...state,
        analyticsData: state.analyticsBackupData
      };
    },
    setActiveReportAnalyticsQueueData: (
      state,
      action: PayloadAction<Analytics>
    ) => {
      const existingIndex = state.analyticsQueueData?.findIndex(
        (item) => item.order === action.payload.order
      );

      if (existingIndex !== -1 && existingIndex !== undefined) {
        // Update existing item
        const updatedAnalyticsData = [...state.analyticsQueueData];
        updatedAnalyticsData[existingIndex] = action.payload;
        return {
          ...state,
          analyticsQueueData: updatedAnalyticsData
        };
      }

      // If no matching order found, append
      return {
        ...state,
        analyticsQueueData: state.analyticsQueueData
          ? [...state.analyticsQueueData, action.payload]
          : [action.payload]
      };
    },

    setBackupAnalyticsQueue: (state, action: PayloadAction<Analytics>) => {
      return {
        ...state,
        analyticsQueueBackupData: state.analyticsQueueBackupData
          ? [...state.analyticsQueueBackupData, action.payload]
          : [action.payload]
      };
    },
    setAnalyticsQueueWithBackup: (state) => {
      return {
        ...state,
        analyticsQueueData: state.analyticsQueueBackupData
      };
    },
    callAnalyticsQueueApi: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        callAnalyticsQueueApi: action.payload
      };
    },
    setActiveReportContentTitleList: (
      state,
      action: PayloadAction<ReportContentTitles[]>
    ) => {
      return {
        ...state,
        reportContentTitleList: action.payload,
        initialReportContentTitleList: action.payload
      };
    },
    updateContentTitle: (state, action: PayloadAction<ReportContentTitles>) => {
      const allData = [...state.reportContentTitleList];
      const dataIndex = state.reportContentTitleList.findIndex(
        (item) => item.order === action.payload.order
      );
      if (dataIndex > -1) {
        allData[dataIndex] = action.payload;
      } else {
        allData.push(action.payload);
      }
      return {
        ...state,
        reportContentTitleList: allData
      };
    },
    removeContentTitle: (state, action: PayloadAction<ReportContentTitles>) => {
      const allTitles = [...state.reportContentTitleList];
      const indexOfRemoval = allTitles.findIndex(
        (item) => item.order === action.payload.order
      );
      if (indexOfRemoval > 0) allTitles.splice(indexOfRemoval, 1);
      return {
        ...state,
        reportContentTitleList: allTitles
      };
    },
    resetTitlesWithInitial: (state) => {
      return {
        ...state,
        reportContentTitleList: state.initialReportContentTitleList
      };
    }
  }
});

export const {
  setActiveReport,
  setActiveReportGraphStatistics,
  setActiveReportAnalyticsData,
  resetAnalyticsData,
  analyticsLoadingAction,
  setBackupAnalytics,
  setAnalyticsWithBackup,
  setActiveReportAnalyticsQueueData,
  setBackupAnalyticsQueue,
  setAnalyticsQueueWithBackup,
  callAnalyticsQueueApi,
  setActiveReportContentTitleList,
  updateContentTitle,
  removeContentTitle,
  resetTitlesWithInitial
} = reportSlice.actions;

export default reportSlice.reducer;
